// src/prototype/components/EALockedOpportunityRow.tsx
// ============================================================
// FREEMIUM LOCK AFFORDANCE — Use Case Explorer row beyond the top-N.
//
// The row stays visible in the ranked list (rank + title + category) so the
// full ranking is legible, but its detail and actions are locked behind an
// upgrade CTA that routes to /upgrade (the conversion surface).
// ============================================================

import React from 'react';
import { Lock, Zap } from 'lucide-react';
import { EXPLORER_COLS, EXPLORER_SPACERS } from './explorerColumns';

interface EALockedOpportunityRowProps {
  rank: number;
  title: string;
  category: string;
  /** Routes to the upgrade / conversion surface. */
  onUpgrade: () => void;
}

export function EALockedOpportunityRow({
  rank,
  title,
  category,
  onUpgrade,
}: EALockedOpportunityRowProps) {
  return (
    <div
      onClick={onUpgrade}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onUpgrade();
        }
      }}
      className={`${EXPLORER_COLS.track} py-3 cursor-pointer border-l-4 border-l-transparent
                 hover:bg-[#F5F7FA] transition-all group`}
    >
      {/* Muted rank badge keeps the list numbering continuous */}
      <div className={`${EXPLORER_COLS.rank} relative flex items-center justify-center h-9 rounded-full bg-[#18365E]/35 text-white font-bold text-base`}>
        {rank}
      </div>

      <div className={EXPLORER_COLS.title}>
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-bold text-[#18365E]/55 leading-tight truncate">
            {title}
          </h3>
          <Lock className="w-3.5 h-3.5 text-[#717182] shrink-0" />
        </div>
        <span className="text-[10px] font-semibold text-[#717182] bg-[#F5F7FA] border border-[#e5e7eb] px-2 py-0.5 rounded mt-1 inline-block">
          {category}
        </span>
      </div>

      {/* A locked row has no value / TTV / effort to show, but it still has to keep
          the columns after them on the grid — otherwise the upgrade CTA lands at a
          different x than the unlocked rows' "Open brief" and the freemium list
          reads as a different table from the pro one. */}
      {EXPLORER_SPACERS.map((col) => (
        <div key={col} className={col} aria-hidden="true" />
      ))}

      <div className={EXPLORER_COLS.actions}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUpgrade();
          }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold shrink-0
                     bg-gradient-to-r from-[#FF6E4C] to-[#FF8F73] text-white
                     hover:from-[#FF5C3A] hover:to-[#FF7D61] shadow-sm transition-all"
        >
          <Zap className="w-3.5 h-3.5" />
          Upgrade to unlock
        </button>
      </div>
    </div>
  );
}

export default EALockedOpportunityRow;
